import {
  BoundCheckboxField,
  BoundDateField,
  BoundMultiSelectField,
  BoundSelectField,
  Button,
  collapseColumn,
  column,
  Css,
  emptyCell,
  fieldCell,
  fieldColumn,
  GridTableLayout,
  type HeaderAction,
  IconButton,
  MaxLines,
  Palette,
  simpleHeader,
  Tag,
  Tooltip,
  type useGridTableLayoutState,
} from "@homebound/beam";
import { type ObjectState, useFormStates } from "@homebound/form-state";
import DOMPurify from "dompurify";
import type { Maybe } from "graphql/jsutils/Maybe";
import { Observer } from "mobx-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CommentCountBubble } from "src/components/comments/CommentCountBubble";
import { useSaveFollowersMutation } from "src/components/comments/CommentFeed.generated";
import { ToDoChecklistStatus } from "src/components/to-dos/ToDoChecklistStatus";
import type { ParentOption } from "src/components/to-dos/ToDoModal";
import { useSaveToDosMutation } from "src/components/to-dos/ToDoModal.generated";
import type { ToDoFilterWithDateFilterValue } from "src/components/to-dos/ToDosBoard";
import type { ToDoCardFragment } from "src/components/to-dos/ToDosBoard.generated";
import { type SaveToDoInput, ToDoStatus, type ToDoStatusDetail, type ToDoTypeDetail } from "src/generated/graphqlTypes";
import type { AvailableTeamMembersFragment } from "src/routes/projects/settings/TeamMembersTab.generated";
import { createDashboardTodoModalUrl, createProjectToDoModalUrl } from "src/RouteUrls";
import { sortBy } from "src/utils";
import { internalUserAvatar, internalUserMenuLabel } from "src/utils/decorators/internalUserDecorators";
import type { ObjectConfig } from "src/utils/formState";

export type ToDosTableProps = {
  projectId?: string;
  toDos: ToDoCardFragment[];
  parentOptions: ParentOption[];
  teamMembers: AvailableTeamMembersFragment[];
  types: ToDoTypeDetail[];
  fetchMoreCompletedTasks: () => void;
  showLoadMore: boolean;
  loading?: boolean;
  error?: { message: string };
  layoutState?: ReturnType<typeof useGridTableLayoutState<ToDoFilterWithDateFilterValue>>;
  actions?: HeaderAction[];
};

/**
 * Renders grouped To-Dos with inline editing and standard Beam table controls.
 * Saves each row independently and opens details from its title and action icons.
 */
export function ToDosTable(props: ToDosTableProps) {
  const {
    projectId,
    toDos,
    parentOptions,
    teamMembers,
    types,
    fetchMoreCompletedTasks,
    showLoadMore,
    loading = false,
    error,
    layoutState,
    actions,
  } = props;
  const [saveToDosMutation] = useSaveToDosMutation();
  const [saveFollowersMutation] = useSaveFollowersMutation();
  const { getFormState } = useFormStates<InLineSaveToDo, ToDoCardFragment>({
    config: formConfig,
    map: (todo) => ({
      id: todo.id,
      dueDate: todo.dueDate,
      urgent: todo.urgent,
      status: todo.status.code,
      parentId: todo.parent?.id,
      description: todo.description,
      assigneeIds: todo.assignees.flatMap((assignee) => (assignee.internalUser ? [assignee.internalUser.id] : [])),
      followerIds: todo.followers.map((f) => f.id),
      type: todo.type.code,
      complete: todo.status.code === ToDoStatus.Complete,
    }),
    getId: (o) => o.id!,
    autoSave: async (os) => saveTodo(os),
  });
  const navigate = useNavigate();

  // helper to toggle status of checkbox field to complete or in progress
  async function toggleStatus(os: ObjectState<InLineSaveToDo>) {
    const { id, complete, ...input } = os.changedValue;
    await saveToDosMutation({
      variables: { toDos: [{ id, status: complete ? ToDoStatus.Complete : ToDoStatus.InProgress, ...input }] },
    });
  }

  /** Saves changed To-Do fields and watcher selections. */
  async function saveTodo(os: ObjectState<InLineSaveToDo>) {
    if (!os.canSave()) return;
    const { id, followerIds, complete, ...input } = os.changedValue;
    if (Object.keys(input).nonEmpty) {
      await saveToDosMutation({ variables: { toDos: [{ id, ...input }] } });
    }
    if (followerIds) {
      await saveFollowersMutation({ variables: { input: { followableId: id!, followerIds, fromTag: false } } });
      os.followerIds.commitChanges();
    }
  }

  const openTodoModal = useCallback(
    (id: string, tab?: string) => {
      const maybeTabParam = tab ? { search: `?tab=${tab}` } : {};

      navigate({
        pathname: !!projectId ? createProjectToDoModalUrl(projectId, id) : createDashboardTodoModalUrl(id),
        ...maybeTabParam,
      });
    },
    [projectId, navigate],
  );

  const sortedParentOption = useMemo(() => sortBy(parentOptions, (p) => p.name), [parentOptions]);

  const columns = [
    collapseColumn<Row>(),
    ...(!projectId
      ? [
          column<Row>({
            header: "Project/Cohort",
            status: (data) => renderStatusHeader(data, 8),
            todo: (todo) =>
              fieldCell({
                content: () => {
                  const os = getFormState(todo);
                  return (
                    <BoundSelectField
                      label="Parent"
                      labelStyle="hidden"
                      field={os.parentId}
                      options={sortedParentOption}
                    />
                  );
                },
              }),
            loadMore: () => emptyCell,
          }),
        ]
      : []),
    column<Row>({
      header: "Title",
      status: projectId ? (data) => renderStatusHeader(data, 7) : emptyCell,
      w: "240px",
      todo: (todo) => {
        const os = getFormState(todo);
        // The complete checkbox used to live in the collapse column. Beam draws that column inside the
        // first content cell and replaces it with a spacer on rows that have no chevron, which hid the checkbox.
        return (
          <div css={Css.df.aic.gap1.$}>
            <BoundCheckboxField
              field={os.complete}
              checkboxOnly
              onChange={async () => {
                os.complete.set(!os.complete.value);
                await toggleStatus(os);
              }}
            />
            <Button
              variant="text"
              label={todo.name}
              data-testid="name"
              onClick={projectId ? createProjectToDoModalUrl(projectId, todo.id) : createDashboardTodoModalUrl(todo.id)}
            />
          </div>
        );
      },
      loadMore: () => emptyCell,
    }),
    column<Row>({
      header: "Description",
      status: emptyCell,
      w: "240px",
      todo: (todo) => (
        <MaxLines maxLines={2}>
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(todo.description ?? "", { USE_PROFILES: { html: false } }),
            }}
          />
        </MaxLines>
      ),
      loadMore: () => emptyCell,
    }),
    fieldColumn<Row>({
      header: "Assignees",
      status: emptyCell,
      todo: (todo) => {
        const os = getFormState(todo);
        return (
          <BoundMultiSelectField
            label="Assignees"
            labelStyle="hidden"
            field={os.assigneeIds}
            options={teamMembers}
            fieldDecoration={internalUserAvatar}
            getOptionMenuLabel={internalUserMenuLabel}
          />
        );
      },
      loadMore: () => ({
        content: showLoadMore && (
          <Button variant="secondary" label="Load More" disabled={loading} onClick={fetchMoreCompletedTasks} />
        ),
        colspan: 5,
      }),
    }),
    fieldColumn<Row>({
      header: "Due Date",
      status: emptyCell,
      todo: (todo) => {
        const os = getFormState(todo);
        return <BoundDateField label="Due Date" labelStyle="hidden" field={os.dueDate} />;
      },
      loadMore: emptyCell,
    }),
    fieldColumn<Row>({
      header: "Watchers",
      status: emptyCell,
      todo: (todo) => {
        const os = getFormState(todo);
        return (
          <BoundMultiSelectField
            label="Watchers"
            labelStyle="hidden"
            field={os.followerIds}
            options={teamMembers}
            fieldDecoration={internalUserAvatar}
            getOptionMenuLabel={internalUserMenuLabel}
          />
        );
      },
      loadMore: emptyCell,
    }),
    fieldColumn<Row>({
      header: "Type",
      status: emptyCell,
      todo: (todo) => {
        const os = getFormState(todo);
        return (
          <BoundSelectField
            label="Type"
            labelStyle="hidden"
            options={types}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.code}
            field={os.type}
          />
        );
      },
      loadMore: emptyCell,
    }),
    fieldColumn<Row>({
      header: emptyCell,
      w: "140px",
      sticky: "right",
      status: emptyCell,
      todo: (todo) => {
        const { id, streams, checklistProgress } = todo;
        const os = getFormState(todo);
        return (
          <div css={Css.df.aic.gap1.jcfe.$}>
            <Observer>
              {() => (
                <Tooltip title={os.urgent.value ? "Urgent" : "Not Urgent"}>
                  <div>
                    <IconButton
                      icon={os.urgent.value ? "flag" : "outlineFlag"}
                      color={os.urgent.value ? Palette.Red600 : undefined}
                      onClick={() => os.urgent.set(!os.urgent.value)}
                    />
                  </div>
                </Tooltip>
              )}
            </Observer>
            <CommentCountBubble streams={streams} onClick={() => openTodoModal(id)} />
            <ToDoChecklistStatus checklistProgress={checklistProgress} openTodoModal={openTodoModal} toDoId={id} />
          </div>
        );
      },
      loadMore: emptyCell,
    }),
  ];

  const rows = useMemo(() => createToDoRows(toDos, showLoadMore), [toDos, showLoadMore]);

  return (
    <GridTableLayout
      layoutState={layoutState}
      actions={actions}
      hideEditColumns
      tableProps={{
        as: "virtual",
        query: { data: toDos, loading, error },
        createRows: () => rows,
        columns,
        style: { inlineEditing: true },
        emptyFallback: "No to-dos found.",
      }}
    />
  );
}

type InLineSaveToDo = SaveToDoInput & { followerIds: Maybe<string[]>; complete: Maybe<boolean> };

const formConfig: ObjectConfig<InLineSaveToDo> = {
  id: { type: "value" },
  status: { type: "value" },
  urgent: { type: "value" },
  type: { type: "value" },
  parentId: { type: "value" },
  description: { type: "value" },
  assigneeIds: { type: "value" },
  dueDate: { type: "value" },
  followerIds: { type: "value" },
  complete: { type: "value" },
};

type HeaderRow = { kind: "header"; data: undefined };
type StatusRow = { kind: "status"; data: { status: ToDoStatusDetail; count: number } };
type ToDoRow = { kind: "todo"; data: ToDoCardFragment };
type LoadMoreRow = { kind: "loadMore"; data: string };
type Row = HeaderRow | StatusRow | ToDoRow | LoadMoreRow;

/** Displays the status once per group, spanning the columns after its collapse toggle. */
function renderStatusHeader(data: StatusRow["data"], colspan: number) {
  return {
    content: () => (
      <div css={Css.df.aic.gap1.$}>
        <span>{data.status.name}</span>
        <Tag text={data.count.toString()} variant="secondary" />
      </div>
    ),
    colspan,
  };
}

/**
 * Groups To-Dos by status, with completed To-Dos last.
 * Preserves descending To-Do order within each group so new To-Dos appear first.
 */
function createToDoRows(toDos: ToDoCardFragment[], showLoadMore: boolean) {
  const groupedToDos = toDos.groupBy((todo) => todo.status.code);
  return [
    simpleHeader,
    ...[ToDoStatus.InProgress, ToDoStatus.UpNext, ToDoStatus.Backlog, ToDoStatus.Complete].flatMap((code) => {
      const todos = groupedToDos[code];
      if (!todos?.length) return [];
      return [
        {
          id: code,
          kind: "status" as const,
          data: { status: todos[0].status, count: todos.length },
          children: [
            ...todos
              .toSorted((a, b) => b.order.localeCompare(a.order))
              .map((todo) => ({ id: todo.id, kind: "todo" as const, data: todo })),
            ...(code === ToDoStatus.Complete && showLoadMore
              ? [{ id: "loadMore", kind: "loadMore" as const, data: "" }]
              : []),
          ],
        },
      ];
    }),
  ];
}
